/**
 * Wait for an element (matching a selector or specific HTMLElement) to be added to the DOM, then run a callback function.
 */
export function onElementAdded(
	selectorOrElement: string | HTMLElement,
	callback: (element: HTMLElement) => void,
	observeOnce: boolean = true,
) {
	// Create a MutationObserver to watch for added nodes
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((addedNode) => {
				if (addedNode instanceof HTMLElement) {
					// Check if selector is a string (CSS selector) or an HTMLElement
					if (typeof selectorOrElement === 'string') {
						// Handle case where selectorOrElement is a string (CSS selector)
						if (addedNode.matches(selectorOrElement)) {
							callback(addedNode);
							if (observeOnce) observer.disconnect(); // Stop observing after the first match
						} else {
							// Check if any of the added node's children match the selector
							const matchingChildren = addedNode.querySelectorAll(selectorOrElement);
							matchingChildren.forEach((child) => {
								callback(child as HTMLElement);
								if (observeOnce) observer.disconnect(); // Stop observing after the first match
							});
						}
					} else {
						// Handle case where selectorOrElement is an HTMLElement
						if (addedNode === selectorOrElement || addedNode.contains(selectorOrElement)) {
							callback(addedNode);
							if (observeOnce) observer.disconnect(); // Stop observing after the first match
						}
					}
				}
			});
		});
	});

	// Start observing the DOM for added nodes
	observer.observe(document.body, {
		childList: true,
		subtree: true // Also observe children of added nodes
	});
}

/**
 * Wait for an element (matching a selector or specific HTMLElement) to be removed from the DOM, then run a callback function.
 */
export function onElementRemoved(
	selectorOrElement: string | HTMLElement,
	callback: (element: HTMLElement) => void,
	observeOnce: boolean = true,
) {
	// Create a MutationObserver to watch for removed nodes
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.removedNodes.forEach((removedNode) => {
				if (removedNode instanceof HTMLElement) {
					// Check if selector is a string (CSS selector) or an HTMLElement
					if (typeof selectorOrElement === 'string') {
						// Handle case where selectorOrElement is a string (CSS selector)
						if (removedNode.matches(selectorOrElement)) {
							callback(removedNode);
							if (observeOnce) observer.disconnect(); // Stop observing after the first match
						} else {
							// Check if any of the removed node's children match the selector
							const matchingChildren = removedNode.querySelectorAll(selectorOrElement);
							matchingChildren.forEach((child) => {
								callback(child as HTMLElement);
								if (observeOnce) observer.disconnect(); // Stop observing after the first match
							});
						}
					} else {
						// Handle case where selectorOrElement is an HTMLElement
						if (removedNode === selectorOrElement || removedNode.contains(selectorOrElement)) {
							callback(removedNode);
							if (observeOnce) observer.disconnect(); // Stop observing after the first match
						}
					}
				}
			});
		});
	});

	// Start observing the DOM for removed nodes
	observer.observe(document.body, {
		childList: true,
		subtree: true // Also observe removals in the entire subtree
	});
}

/**
 * Debounce a function to prevent it from being called too frequently. (ex. for input events)
 */
export function debounce(func: (...args: any[]) => void, wait: number) {
	let timeout: number | undefined;
	return function(this: any, ...args: any[]) {
		clearTimeout(timeout);
		timeout = window.setTimeout(() => func.apply(this, args), wait);
	};
}

/* function doubleScroll(element: HTMLElement) {
	const scrollbar = document.createElement("div") as HTMLElement;
	scrollbar.appendChild(document.createElement("div"));
	scrollbar.style.overflow = "auto";
	scrollbar.style.overflowY = "hidden";
	(scrollbar.firstChild as HTMLElement).style.width = element.scrollWidth + "px";
	(scrollbar.firstChild as HTMLElement).style.paddingTop = "1px";
	(scrollbar.firstChild as HTMLElement).appendChild(document.createTextNode("\xA0"));
	let running = false;
	// Keep scrollbar in sync when element size changes
	new ResizeObserver(() => {
		if (scrollbar.firstChild) {
			(scrollbar.firstChild as HTMLElement).style.width = element.scrollWidth + "px";
		}
	}).observe(element);
	scrollbar.onscroll = function () {
		if (running) {
			running = false;
			return;
		}
		running = true;
		element.scrollLeft = scrollbar.scrollLeft;
	};
	element.onscroll = function () {
		if (running) {
			running = false;
			return;
		}
		running = true;
		scrollbar.scrollLeft = element.scrollLeft;
	};
	if (element.parentNode) {
		element.parentNode.insertBefore(scrollbar, element);
	}
}

doubleScroll(document.querySelector(".tablelist")!); */